import {
  FileTextIcon,
  GlobeIcon,
  PhoneIcon,
  ScanIcon,
  MailIcon,
  MessageCircleIcon,
  FolderIcon,
  CodeIcon,
  PillIcon,
  ChromeIcon,
  CalendarIcon,
  ActivityIcon,
} from "lucide-react";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export function NavAgents({
  onNavigate,
  activePage,
}: {
  onNavigate: (page: string) => void;
  activePage: string;
}) {
  // Check feature flags for each tool
  const emailFlag = useFeatureFlag("feature_email");
  const scannerFlag = useFeatureFlag("feature_scanner");
  const browserFlag = useFeatureFlag("feature_browser");
  const documentGeneratorFlag = useFeatureFlag("feature_document_generator");
  const callSmsFlag = useFeatureFlag("feature_call_sms");
  const chatFlag = useFeatureFlag("feature_chat");
  const desktopWriteFlag = useFeatureFlag("feature_desktop_write");
  const formularyFlag = useFeatureFlag("feature_formulary");
  const medReviewFlag = useFeatureFlag("feature_med_review");
  const schedulerFlag = useFeatureFlag("feature_scheduler");

  // Check if any flags are still loading
  const loading = [
    emailFlag,
    scannerFlag,
    browserFlag,
    documentGeneratorFlag,
    callSmsFlag,
    chatFlag,
    desktopWriteFlag,
    medReviewFlag,
    schedulerFlag,
  ].some((flag) => flag.loading);

  const agentItems = [
    {
      name: "Email",
      id: "mail",
      url: "#",
      icon: MailIcon,
      enabled: emailFlag.enabled,
    },
    {
      name: "Data Extraction",
      id: "scanner",
      url: "#",
      icon: ScanIcon,
      enabled: scannerFlag.enabled,
    },
    {
      name: "Browser",
      id: "browser",
      url: "#",
      icon: GlobeIcon,
      enabled: browserFlag.enabled,
    },
    {
      name: "Document Generator",
      id: "document-generator",
      url: "#",
      icon: FileTextIcon,
      enabled: documentGeneratorFlag.enabled,
    },
    {
      name: "Call/SMS",
      id: "call-sms",
      url: "#",
      icon: PhoneIcon,
      enabled: callSmsFlag.enabled,
    },
    {
      name: "Chat",
      id: "",
      url: "#",
      icon: MessageCircleIcon,
      enabled: chatFlag.enabled,
    },
    {
      name: "Application Writing",
      id: "desktop-write",
      url: "#",
      icon: CodeIcon,
      enabled: desktopWriteFlag.enabled,
    },
    {
      name: "Formulary",
      id: "formulary",
      url: "#",
      icon: PillIcon,
      enabled: formularyFlag.enabled,
    },
    {
      name: "Medication Review",
      id: "med-review",
      url: "#",
      icon: ActivityIcon,
      enabled: true, // Enable by default - newly migrated feature
    },
    {
      name: "Scheduler",
      id: "scheduler",
      url: "#",
      icon: CalendarIcon,
      enabled: true, // Enable by default - newly migrated feature
    },
  ];

  // Filter to only show enabled items
  const visibleItems = agentItems.filter((item) => item.enabled);

  // Show loading state while feature flags are loading
  if (loading) {
    return (
      <SidebarGroup>
        <SidebarGroupLabel>Manage tools</SidebarGroupLabel>
        <SidebarGroupContent className="py-0.5">
          <SidebarMenu>
            <div className="text-sm text-muted-foreground px-2 py-1">
              Loading tools...
            </div>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Manage tools</SidebarGroupLabel>
      <SidebarGroupContent className="py-0.5">
        <SidebarMenu>
          {visibleItems.map((item) => (
            <SidebarMenuItem key={item.name}>
              <SidebarMenuButton
                asChild
                isActive={activePage === item.id}
                onClick={() => onNavigate(item.id)}
              >
                <a href={item.url} onClick={(e) => e.preventDefault()}>
                  <item.icon />
                  <span>{item.name}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
